import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';

import GroupRow from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/components/group-row';
import RegimeRow from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/components/regime-row';
import RegimeActionModal from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/components/regime-action-modal';
import RegimesSidebarToggle from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/components/regimes-sidebar-toggle';

import {
  useRegimesSidebarActions,
  type RegimeGroup,
  type RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/use-regimes-sidebar-actions';
import { useRegimeSelection } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-selection-context';

import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';

import Icons from 'assets/icons';

const DND_MODIFIERS = [restrictToVerticalAxis];

type RegimesSidebarProps = {
  userId: string;
  modelId: string | number;
  groups: RegimeGroup[];
  regimes: RegimeItem[];
  onAddRegime: () => void;
  hideToggle: boolean;
  onToggleCollapse: () => void;
  focusToggleOnMount?: boolean;
  onGuardAction?: (
    action: () => void,
    affectedRegimeId?: string | number
  ) => void;
  readOnly?: boolean;
};

const DUPLICATE_LABEL = 'Duplicate';

const RegimesSidebar = ({
  userId,
  modelId,
  groups,
  regimes,
  onAddRegime,
  hideToggle,
  onToggleCollapse,
  focusToggleOnMount,
  onGuardAction,
  readOnly,
}: RegimesSidebarProps) => {
  const [expandedGroups, setExpandedGroups] = useState<
    Record<string | number, boolean>
  >({});
  const { selectedRegimeId } = useRegimeSelection();

  const {
    groupsWithRegimes,
    sensors,
    activeDragData,
    modalState,
    isModalLoading,
    hasPendingGroupUpdate,
    isRegimePending,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    handleGroupToggle,
    handleModalConfirm,
    buildGroupActions,
    buildRegimeActions,
    closeModal,
  } = useRegimesSidebarActions({
    userId,
    modelId,
    groups,
    regimes,
    expandedGroupIds: expandedGroups,
  });

  const wrapDuplicate = useCallback(
    (
      actions: ActionsDropdownOption[],
      affectedRegimeId: string | number | null
    ) => {
      if (!onGuardAction || affectedRegimeId == null) return actions;

      return actions.map((action) => {
        if ((action as { label?: string }).label !== DUPLICATE_LABEL)
          return action;

        const originalOnClick = (action as { onClick: () => void }).onClick;

        return {
          ...action,
          onClick: () => onGuardAction(originalOnClick, affectedRegimeId),
        };
      });
    },
    [onGuardAction]
  );

  const guardedBuildGroupActions = useCallback(
    (group: RegimeGroup) => {
      const groupRegimeIds =
        groupsWithRegimes
          .find((g) => g.id === group.id)
          ?.regimes?.map((r) => r.id) ?? [];
      const isGroupContainingSelected =
        selectedRegimeId != null && groupRegimeIds.includes(selectedRegimeId);

      return wrapDuplicate(
        buildGroupActions(group),
        isGroupContainingSelected ? selectedRegimeId : null
      );
    },
    [buildGroupActions, groupsWithRegimes, selectedRegimeId, wrapDuplicate]
  );

  const guardedBuildRegimeActions = useCallback(
    (regime: RegimeItem) =>
      wrapDuplicate(buildRegimeActions(regime), regime.id),
    [buildRegimeActions, wrapDuplicate]
  );

  const activeRegime = useMemo(
    () =>
      activeDragData?.type === 'regime'
        ? groupsWithRegimes
            .flatMap((group) => group.regimes)
            .find((regime) => regime.id === activeDragData.entityId)
        : undefined,
    [activeDragData, groupsWithRegimes]
  );

  const expandedForRegimeRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!selectedRegimeId) {
      expandedForRegimeRef.current = null;

      return;
    }

    if (expandedForRegimeRef.current === selectedRegimeId) return;

    const group = groupsWithRegimes.find((g) =>
      g.regimes?.some((r) => r.id === selectedRegimeId)
    );

    if (group) {
      expandedForRegimeRef.current = selectedRegimeId;
      setExpandedGroups((prev) => {
        if (prev[group.id]) return prev;

        return { ...prev, [group.id]: true };
      });
    }
  }, [selectedRegimeId, groupsWithRegimes]);

  return (
    <Stack
      sx={{
        width: '20rem',
        minHeight: 0,
        flexShrink: 0,
        borderRightWidth: '1px',
        borderColor: 'tokens.navy.200',
        gap: 1.5,
      }}
      data-testid="regimes-sidebar"
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '3rem',
          px: 1.5,
          pt: 1.5,
        }}
      >
        <Box sx={{ width: '2.5rem', height: '2.5rem', flexShrink: 0 }}>
          {!hideToggle && (
            <RegimesSidebarToggle
              isCollapsed={false}
              onToggle={onToggleCollapse}
              focusOnMount={focusToggleOnMount}
            />
          )}
        </Box>
        <Button
          type="button"
          variant="reserve-text"
          iconBefore={<Icons.Plus />}
          onClick={onAddRegime}
          disabled={readOnly}
          data-testid="add-regime-button"
        >
          Add new
        </Button>
      </Stack>

      <span className="px-3 text-base font-bold text-navy-800">Regimes</span>

      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 pb-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          modifiers={DND_MODIFIERS}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Stack sx={{ gap: 0.5 }}>
            {groupsWithRegimes.map((group) => (
              <GroupRow
                key={group.id}
                group={group}
                isExpanded={!!expandedGroups[group.id]}
                onToggleExpand={() =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [group.id]: !prev[group.id],
                  }))
                }
                onToggleEnabled={(checked) => handleGroupToggle(group, checked)}
                groupActions={guardedBuildGroupActions(group)}
                regimeActionsFactory={guardedBuildRegimeActions}
                isToggleDisabled={hasPendingGroupUpdate}
                isRegimeDragDisabled={isRegimePending}
                readOnly={readOnly}
              />
            ))}
          </Stack>

          <DragOverlay dropAnimation={{ duration: 180 }}>
            {activeRegime ? (
              <div className="w-72 rounded-lg bg-white shadow-lg ring-1 ring-navy-100">
                <RegimeRow
                  regime={activeRegime}
                  actions={[]}
                  hideActions
                  sx={{ cursor: 'grabbing' }}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {modalState && (
        <RegimeActionModal
          type={modalState.type}
          entity={modalState.entity}
          onClose={closeModal}
          onConfirm={handleModalConfirm}
          loading={isModalLoading}
        />
      )}
    </Stack>
  );
};

export default RegimesSidebar;
